import gql from 'graphql-tag';
import { compose, graphql } from 'react-apollo';

import OrderView from './OrderView';

const GetOrdersQuery = gql`
  query($orderId: String!) {
    order(orderId: $orderId) {
      id
      createdAt
      orderNumber
      language

      country {
        id
        name
        code
      }

      currency {
        id
        value
      }

      customer {
        id
        email
      }
      externalState {
        id
        title
        color
      }
      actionRecords {
        id
        type
        source
        newValue
        createdAt
        author {
          id
          email
        }
      }
      orderedItems {
        id
        title
        name
        description
        definition
        weight
        ean
        sku
        VATrate
        price
        recommendedPrice
        deliveryPriceAddition
        quantity
        image {
          id
          file
        }
        product {
          id
          weight
          weightForShipping
        }
        additionalDescription
      }

      deliveryMethod {
        id
        title
        price
      }
      paymentMethod {
        id
        title
      }
      vouchers {
        id
        title
        voucherGroup {
          id
          title
        }
      }
      status
      subTotal
      total
      paymentTotal
      deliveryTotal
      deliveryInsuranceTotal
      grandTotal
      tariffTotal
      email
      paid
      phone
      companyInformation
      companyName
      companyRegistrationNumber
      companyVatNumber
      companyVatNumberValid
      invoiceFirstName
      invoiceLastName
      invoiceStreet
      invoiceHouseNumber
      invoiceAddressLine1
      invoiceAddressLine2
      invoiceZip
      invoiceCity
      invoiceCountyState
      invoiceCountry
      anotherDeliveryAddress
      deliveryFirstName
      deliveryLastName
      deliveryPhone
      deliveryStreet
      deliveryHouseNumber
      deliveryAddressLine1
      deliveryAddressLine2
      deliveryZip
      deliveryCity
      deliveryCountyState
      deliveryCountry
      notes
      invoiceNotes
      shippingPackageNumber
      shippingTrackingLink
      recycledBox
    }
  }
`;

const UpdateOrderMutation = gql`
  mutation updateOrder($orderId: String!, $data: UpdateOrderInput) {
    updateOrder(orderId: $orderId, data: $data) {
      id
      deliveryMethod {
        id
        title
        price
      }
      deliveryTotal
      shippingPackageNumber
      shippingTrackingLink
      invoiceNotes
    }
  }
`;

const ChangeOrderStateMutation = gql`
  mutation changeState($newStateId: String!, $orderId: String!, $domain: String!) {
    changeExternalOrderState(newStateId: $newStateId, orderId: $orderId, eshopDomain: $domain) {
      id
      subject
      title
      text
      attachmentInvoice
      attachmentProforma
    }
  }
`;

const CommentsQuery = gql`
  query orderComments($orderId: String!) {
    orderComments(orderId: $orderId) {
      id
      createdAt
      author {
        id
        email
      }
      value
      emailSent
    }
  }
`;

const CreateCommentMutation = gql`
  mutation createComment($value: String!, $sendEmail: Boolean!, $orderId: String!) {
    createOrderComment(value: $value, sendEmail: $sendEmail, orderId: $orderId) {
      id
    }
  }
`;

const CreateOrderItemMutation = gql`
  mutation createOrderItem($orderItem: CreateOrderItemInput!) {
    createOrderItem(orderItem: $orderItem) {
      id
    }
  }
`;

const CreateNonProductOrderItemMutation = gql`
  mutation createNonProductOrderItem($orderItem: CreateNonProductOrderItemInput!) {
    createNonProductOrderItem(orderItem: $orderItem) {
      id
    }
  }
`;

const UpdateOrderItemMutation = gql`
  mutation updateOrderItem($id: String!, $orderItem: UpdateOrderItemInput!) {
    updateOrderItem(id: $id, orderItem: $orderItem) {
      id
    }
  }
`;

const UpdateNonProductOrderItemMutation = gql`
  mutation updateNonProductOrderItem($id: String!, $orderItem: UpdateNonProductOrderItemInput!) {
    updateNonProductOrderItem(id: $id, orderItem: $orderItem) {
      id
    }
  }
`;

const DeleteOrderItemMutation = gql`
  mutation deleteOrderItem($id: String!) {
    deleteOrderItem(id: $id) {
      id
    }
  }
`;

const SendOrderEmailMutation = gql`
  mutation sendOrderEmail(
    $orderId: String!,
    $recipient: String!, 
    $subject: String!, 
    $body: String!
    $attachments: OrderEmailAttachments,
  ) {
    sendOrderEmail(
      orderId: $orderId,
      recipient: $recipient, 
      subject: $subject, 
      body: $body,
      attachments: $attachments,
    )
  }
`;

const GetAttributesQuery = gql`
  query attributes {
    attributes {
      id
      title
      values {
        id
        title
      }
    }
  }
`;

const UploadToPohoda = gql`
  mutation uploadToPohoda(
    $orderId: String!
  ) {
    uploadToPohoda(
      orderId: $orderId
    ) {
      id
    }
  }
`;

const UploadQBPurchaseOrder = gql`
  mutation uploadPurchaseQBOrder(
    $orderId: String!
  ) {
    uploadPurchaseQBOrder(
      orderId: $orderId
    ) {
      id
    }
  }
`;

export default compose(
  graphql<any, GQL.IQuery>(GetOrdersQuery, {
    name: 'order',
    options: props => ({
      variables: {
        orderId: props.match.params.orderId,
        // domain: props.match.params.eshopDomain
      },
      fetchPolicy: 'network-only'
    })
  }),
  graphql<any, GQL.IQuery>(GetAttributesQuery, {
    name: 'attributes',
  }),
  graphql<any, GQL.IQuery>(UpdateOrderMutation, {
    name: 'updateOrder'
  }),
  graphql<any, GQL.IQuery>(ChangeOrderStateMutation, {
    name: 'changeState'
  }),
  graphql<any, GQL.IQuery>(CreateCommentMutation, {
    name: 'createOrderComment'
  }),
  graphql<any, GQL.IQuery>(CreateOrderItemMutation, {
    name: 'createOrderItem'
  }),
  graphql<any, GQL.IQuery>(CreateNonProductOrderItemMutation, {
    name: 'createNonProductOrderItem'
  }),
  graphql<any, GQL.IQuery>(UpdateOrderItemMutation, {
    name: 'updateOrderItem'
  }),
  graphql<any, GQL.IQuery>(UpdateNonProductOrderItemMutation, {
    name: 'updateNonProductOrderItem'
  }),
  graphql<any, GQL.IQuery>(DeleteOrderItemMutation, {
    name: 'deleteOrderItem'
  }),
  graphql<any, GQL.IQuery>(CommentsQuery, {
    name: 'orderComments',
    options: props => ({
      variables: {
        orderId: props.match.params.orderId
      },
      fetchPolicy: 'network-only'
    })
  }),
  graphql<any, GQL.IQuery>(SendOrderEmailMutation, {
    name: 'sendOrderEmail',
    options: props => ({
      fetchPolicy: 'network-only'
    })
  }),
  graphql<any, GQL.IQuery>(UploadToPohoda, {
    name: 'uploadToPohoda'
  }),
  graphql<any, GQL.IQuery>(UploadQBPurchaseOrder, {
    name: 'uploadQBPurchaseOrder'
  })
)(OrderView);



// WEBPACK FOOTER //
// ./src/scenes/OrderView/index.tsx